import { ADD_TYPING_INDICATOR, REMOVE_TYPING_INDICATOR } from './types.js';

// Senders re-send "typing" at most this often while someone keeps typing.
export const TYPING_HEARTBEAT_SECONDS = 2;

// Backup for a lost "stopped typing" event. Must stay above every sender's
// heartbeat (including chekkit_app) or a steady typer's indicator blinks off.
const TYPING_TIMEOUT_MS = 3000;

// Typing events and messages travel separately, so a "typing" sent just before
// Enter can land after the message. Ignore those for a moment after a message.
const LATE_TYPING_GRACE_MS = 1500;

const AI_TYPER_NAME = 'AI Assistant';

const timers = {}; // typer key -> backup timeout

const lastMessageAt = {}; // typer key -> when their last message landed

const latestSignalAt = {}; // typer key -> sender's send time of the newest signal applied

// A single conversation can have multiple people typing, and the same person
// can be typing in more than one conversation, so timers are keyed by the
// conversation AND who is typing. Keying on whoIsTyping alone let these
// collide and clear the wrong indicator.
const getTyperKey = (typingIndicator) =>
	`${
		typingIndicator._customerConversation ||
		typingIndicator._teamConversation ||
		''
	}:${typingIndicator.whoIsTyping}`;

// Signals travel as separate requests and can overtake each other. Each carries
// the sender's send time (`at`), so one older than a signal already applied for
// the same typer is stale. Comparing only one sender's times keeps clock skew
// between devices out of it. Signals without `at` (older apps) always apply.
const isStaleSignal = (typingIndicator) => {
	const { at } = typingIndicator;

	if (!Number.isFinite(at)) {
		return false;
	}

	const key = getTyperKey(typingIndicator);

	if (at < (latestSignalAt[key] ?? -Infinity)) {
		return true;
	}

	latestSignalAt[key] = at;
	return false;
};

export const addTypingIndicator = (newTypingIndicator) => async (dispatch) => {
	const key = getTyperKey(newTypingIndicator);

	if (isStaleSignal(newTypingIndicator)) {
		return;
	}

	if (Date.now() - (lastMessageAt[key] || 0) < LATE_TYPING_GRACE_MS) {
		return;
	}

	dispatch({ type: ADD_TYPING_INDICATOR, payload: newTypingIndicator });

	clearTimeout(timers[key]);
	timers[key] = setTimeout(
		() => dispatch(removeTypingIndicator(newTypingIndicator)),
		TYPING_TIMEOUT_MS
	);
};

export const removeTypingIndicator = (typingIndicator) => async (dispatch) => {
	if (isStaleSignal(typingIndicator)) {
		return;
	}

	dispatch({
		type: REMOVE_TYPING_INDICATOR,
		payload: typingIndicator,
	});

	const key = getTyperKey(typingIndicator);
	clearTimeout(timers[key]);
	delete timers[key];
};

// A message from someone means they've stopped typing, even if their
// "stopped typing" event is late or never arrives.
export const clearTypingIndicatorForMessage =
	({ _customerConversation, _teamConversation, message }) =>
	async (dispatch, getState) => {
		if (!message) {
			return;
		}

		const _sender = message._user?._id || message._user;
		const whoIsTyping =
			message.sender === 'ai'
				? AI_TYPER_NAME
				: message._user?.name ||
					getState().users.find((u) => u._id === _sender)?.name;

		if (!whoIsTyping) {
			return;
		}

		const typingIndicator = _teamConversation
			? { _teamConversation, whoIsTyping }
			: { _customerConversation, whoIsTyping };

		lastMessageAt[getTyperKey(typingIndicator)] = Date.now();
		dispatch(removeTypingIndicator(typingIndicator));
	};
